// AddressForm.tsx
// Nota: Cambios mínimos para evitar loops/race y unificar lógica duplicada.
// - Helpers compartidos DRY para opciones y normalización
// - Guardas en handleChange/handleFormAutoComplete (hash anti-reproceso + early returns)
// - setSelectOptions idempotente (no re-render si no hay cambios reales)
// - Remount por `key` solo si cambian opciones realmente
// - Botón Guardar ahora ejecuta form.submit()

import { RootState, useAppDispatch } from '@store/store.tsx';
import { Alert, Button, Card, Form, Modal, Row, Select, Space, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logError, logInfo } from '@tools/LogsHandler.tsx';
import { EditOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
    Address,
    CityDataType,
    CountryDataType,
    initAddress as initAddressValues,
} from '@eshop/types/location.types.ts';
import { FieldType, FieldValue, OptionType, TreeOptionFieldType } from '@tools/types/common.types.ts';
import { ModernAddressDetails } from '@eshop/components/location/AddressCards.tsx';
import { editAddress, fetchAddresses } from '@store/slices/userSlice.tsx';
import { useSelector } from 'react-redux';
import { conns } from '@cfg/axiosConfig.tsx';
import { getValidateField } from '@tools/utils/common.tsx';
import { GenericForm, toLogicalName } from '@tools/components/commonFields.tsx';
import { LabelType } from '@tools/types/commonFields.types.ts';
import {
    mapStatesToOptions,
    type StateDTO,
    type CountryDTO,
    mapCountries,
} from '@eshop/utils/mapLocationDTO.ts';
import { useAutoSelectSingleOption } from '@eshop/hooks/useCheckoutFlow.ts';

// ------------------------- Helpers compartidos (DRY) -------------------------

type OptsMap = { [key: string]: OptionType[] | TreeOptionFieldType[] | undefined };

const shallowEqOptions = (a?: OptionType[] | TreeOptionFieldType[], b?: OptionType[] | TreeOptionFieldType[]) => {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    // Comparación barata por label+value
    for (let i = 0; i < a.length; i++) {
        if (a[i].label !== b[i].label || a[i].value !== b[i].value) return false;
    }
    return true;
};

// Actualiza selectOptions solo si algo cambió realmente (idempotente)
const mergeSelectOptions = (
    prev: OptsMap,
    partial: Partial<OptsMap>
): OptsMap => {
    let changed = false;
    const next: OptsMap = { ...prev };
    for (const k of Object.keys(partial)) {
        const cur = prev[k];
        const incoming = partial[k];
        if (!shallowEqOptions(cur as OptionType[], incoming as OptionType[])) {
            next[k] = incoming;
            changed = true;
        }
    }
    return changed ? next : prev;
};

const normalizeKeys = (obj: { [k: string]: FieldValue }) =>
    Object.entries(obj).reduce((acc, [k, v]) => {
        acc[toLogicalName(k)] = v;
        return acc;
    }, {} as { [k: string]: FieldValue });

const stripNullsDeep = <T extends Record<string, unknown>>(obj: T): T =>
    Object.fromEntries(
        Object.entries(obj || {}).map(([k, v]) => [k, v === null ? undefined : v])
    ) as T;

// Normaliza null/'' a undefined SOLO para selects de ubicación
const sanitizeSelects = <T extends Record<string, any>>(obj: T): T => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const SELECT_KEYS = new Set(['country', 'state', 'city', 'postcode']);
    const out: any = {}; // eslint-disable-line @typescript-eslint/no-explicit-any
    for (const [k, v] of Object.entries(obj || {})) {
        if (v === null) { out[k] = undefined; continue; }
        if (v === '' && SELECT_KEYS.has(k)) { out[k] = undefined; continue; }
        out[k] = v;
    }
    return out as T;
};

const buildCityOptsByName = (cities: CityDataType[], filterByPostCodeKeys?: string) => {
    const cityMap: Record<string, string[]> = {};
    const filtered = filterByPostCodeKeys
        ? cities.filter((c) => (filterByPostCodeKeys.split('-') || []).includes(String(c.id_localidad)))
        : cities;
    for (const c of filtered) {
        if (!cityMap[c.nombre]) cityMap[c.nombre] = [];
        cityMap[c.nombre].push(String(c.id_localidad));
    }
    return Object.keys(cityMap)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: cityMap[name].join('-') }));
};

const buildPostcodeOpts = (cities: CityDataType[], filterByCityKeys?: string) => {
    const pcMap: Record<string, string[]> = {};
    const filtered = filterByCityKeys
        ? cities.filter((c) => (filterByCityKeys.split('-') || []).includes(String(c.id_localidad)))
        : cities;
    for (const c of filtered) {
        if (!pcMap[c.codigo_postal]) pcMap[c.codigo_postal] = [];
        pcMap[c.codigo_postal].push(String(c.id_localidad));
    }
    return Object.keys(pcMap).map((cp) => ({ label: cp, value: pcMap[cp].join('-') }));
};

// Devuelve true si asignó nuevo id, o undefined si lo limpió, o null si no hizo nada
const assignValue = async (
    updated: Address,
    name: keyof Address,
    value: string | undefined,
    list: OptionType[] | undefined,
    fetch?: (filters: { [k: string]: string | number | undefined }) => Promise<void>,
    filters?: { [k: string]: string | number | undefined }
): Promise<string | number | undefined | null> => {
    if (!value) {
        if (updated[name] !== undefined) {
            // limpiar
            updated[name] = undefined;
            return undefined;
        }
        return null;
    }
    if (!list || list.length === 0) return null;
    const id = getValidateField(value, list) as string | number | undefined;
    if (id) {
        updated[name] = id;
        if (fetch) await fetch({ ...(filters || {}), [name]: id });
        return id;
    } else {
        updated[name] = undefined;
        return undefined;
    }
};

const isOptionValue = (value: FieldValue, options?: OptionType[]): boolean => {
    if (value === undefined || value === null || !options?.length) return false;
    return options.some((option) => String(option.value) === String(value));
};

// ------------------------- Helper común: handleChange -------------------------

type HandleChangeParams = {
    name: string;
    value: FieldValue;
    label?: LabelType;
    editedAddress: Address;
    setEditedAddress: React.Dispatch<React.SetStateAction<Address | (Address & { branch_name?: string })>>;
    cities: CityDataType[];
    setSelectOptions: React.Dispatch<React.SetStateAction<OptsMap>>;
    setCities: React.Dispatch<React.SetStateAction<CityDataType[]>>;
    onChange?: (name: string, value: FieldValue, label?: LabelType) => void;
    fetchStates: (args: { country?: string }) => Promise<void>;
    fetchCities: (args: { country?: string; state?: string }) => Promise<void>;
    resetCityPostcodeOptions: () => void;
};

const handleChangeHelper = ({
    name,
    value,
    label,
    editedAddress,
    setEditedAddress,
    cities,
    setSelectOptions,
    setCities,
    onChange,
    fetchStates,
    fetchCities,
    resetCityPostcodeOptions,
}: HandleChangeParams) => {
    const logicalName = toLogicalName(name);
    const safeValue = (value === '' ? undefined : value) as FieldValue;

    // Evitar loops por eventos vacíos
    if (typeof safeValue === 'undefined' && typeof label === 'undefined' && editedAddress[logicalName as keyof Address] === undefined) {
        return;
    }
    if (editedAddress[logicalName as keyof Address] === safeValue) return;

    setEditedAddress((prev) => {
        const updated = { ...prev, [logicalName]: safeValue as string | number | undefined };

        if (logicalName === 'country') {
            updated.state = undefined;
            updated.city = undefined;
            updated.postcode = undefined;
        } else if (logicalName === 'state') {
            updated.city = undefined;
            updated.postcode = undefined;
        }

        if (label) {
            (updated as Address).verbose = {
                ...((prev as Address).verbose || { city: '', state: '', postcode: '', country: '' }),
                [logicalName]: label,
            };
        }
        return updated;
    });

    onChange?.(logicalName, safeValue, label);

    const newAddress: Address = {
        ...editedAddress,
        [logicalName]: safeValue as string | number | undefined,
    } as Address;

    if (logicalName === 'country') {
        newAddress.state = undefined;
        newAddress.city = undefined;
        newAddress.postcode = undefined;

        // ✅ CORRECCIÓN: Primero limpia opciones, luego fetch
        setSelectOptions((prevOpts) => mergeSelectOptions(prevOpts, { state: [], city: [], postcode: [] }));
        setCities([]);

        // El fetchStates manejará la actualización de opciones cuando termine
        fetchStates({ country: safeValue as string }).then();

    } else if (logicalName === 'state') {
        newAddress.city = undefined;
        newAddress.postcode = undefined;

        if (safeValue) {
            // ✅ CORRECCIÓN: Primero limpia opciones, luego fetch
            setSelectOptions((prevOpts) => mergeSelectOptions(prevOpts, { city: [], postcode: [] }));
            setCities([]);

            // El useEffect de cities se encargará de poblar las opciones cuando termine fetchCities
            fetchCities({ country: newAddress.country as string, state: safeValue as string }).then();
        } else {
            // Si no hay state seleccionado, limpia todo
            setSelectOptions((prevOpts) => mergeSelectOptions(prevOpts, { city: [], postcode: [] }));
            setCities([]);
        }
    } else if (logicalName === 'city') {
        if (safeValue) {
            const postOpts = buildPostcodeOpts(cities, safeValue as string);
            setSelectOptions((prev) => mergeSelectOptions(prev, { postcode: postOpts }));
        } else {
            if (newAddress.postcode) {
                const cityOpts = buildCityOptsByName(cities, newAddress.postcode as string);
                const allPost = buildPostcodeOpts(cities);
                setSelectOptions((prev) => mergeSelectOptions(prev, { city: cityOpts, postcode: allPost }));
            } else {
                resetCityPostcodeOptions();
            }
        }
    } else if (logicalName === 'postcode') {
        if (safeValue) {
            const cityOpts = buildCityOptsByName(cities, safeValue as string);
            setSelectOptions((prev) => mergeSelectOptions(prev, { city: cityOpts }));
        } else {
            if (newAddress.city) {
                const postOpts = buildPostcodeOpts(cities, newAddress.city as string);
                const allCities = buildCityOptsByName(cities);
                setSelectOptions((prev) => mergeSelectOptions(prev, { postcode: postOpts, city: allCities }));
            } else {
                resetCityPostcodeOptions();
            }
        }
    }
};

// ------------------------- Helper común: handleFormAutoComplete -------------------------

type HandleFormACParams = {
    formValues: { [key: string]: FieldValue };
    editedAddress: Address;
    setEditedAddress: React.Dispatch<React.SetStateAction<Address | (Address & { branch_name?: string })>>;
    setLocalInitAddress?: React.Dispatch<React.SetStateAction<Address | (Address & { branch_name?: string })>>;
    selectOptions: OptsMap;
    setSelectOptions: React.Dispatch<React.SetStateAction<OptsMap>>;
    cities: CityDataType[];
    fetchStates: (args: { country?: string }) => Promise<void>;
    fetchCities: (args: { country?: string; state?: string }) => Promise<void>;
    setPendingAutocomplete: React.Dispatch<React.SetStateAction<{ [key: string]: FieldValue } | null>>;
    lastAutoHashRef: React.RefObject<string>;
    setKey?: React.Dispatch<React.SetStateAction<number>>;
    loadingCities?: boolean;
    initialGuardAddress?: Address; // si se pasa, y coincide con editedAddress, se hace early return (caso Branch)
};

const handleFormAutoCompleteHelper = async ({
    formValues,
    editedAddress,
    setEditedAddress,
    setLocalInitAddress,
    selectOptions,
    setSelectOptions,
    cities,
    fetchStates,
    fetchCities,
    setPendingAutocomplete,
    lastAutoHashRef,
    setKey,
    loadingCities,
    initialGuardAddress,
}: HandleFormACParams) => {
    // Guard opcional (Branch)
    if (initialGuardAddress && JSON.stringify(initialGuardAddress) === JSON.stringify(editedAddress)) return;

    const norm = sanitizeSelects(normalizeKeys(formValues));
    const { country, state, city, postcode } = norm;

    const autoHash = JSON.stringify({ country, state, city, postcode });
    if (lastAutoHashRef.current === autoHash) return;
    lastAutoHashRef.current = autoHash;

    logInfo('Inicia handleFormAutoComplete - normalizedValues: ', norm, 'editedAddress: ', editedAddress);

    // Si está cargando ciudades, o piden city/postcode sin cities cargadas (y ya hay estados), difiere
    if (
        loadingCities ||
        (cities.length === 0 && (city || postcode) && (state || editedAddress.state) && (selectOptions.state?.length ?? 0) > 0)
    ) {
        setPendingAutocomplete(norm);
        // Si tenemos datos mínimos, pedimos ciudades para el próximo tick
        const countryForFetch = String(country || editedAddress.country || '');
        const stateForFetch = String(state || editedAddress.state || '');
        if (countryForFetch && stateForFetch) {
            await fetchCities({ country: countryForFetch, state: stateForFetch });
        }
        return;
    }

    const updated: Address = { ...editedAddress };

    // País
    if (!updated.country && typeof country === 'string' && (selectOptions['country'] as OptionType[] | undefined)) {
        await assignValue(updated, 'country', country as string, selectOptions['country'] as OptionType[], fetchStates);
    }

    // Estado
    const stateInput = typeof state === 'string' ? state : updated.verbose?.state;
    if ((!updated.state || !isOptionValue(updated.state, selectOptions['state'] as OptionType[] | undefined)) && (updated.country || country) && typeof stateInput === 'string') {
        if (!selectOptions['state'] || (selectOptions['state'] as OptionType[]).length === 0) {
            await fetchStates({ country: String(updated.country || country) });
            await new Promise((r) => setTimeout(r, 80));
        }
        await assignValue(
            updated,
            'state',
            stateInput,
            selectOptions['state'] as OptionType[],
            fetchCities,
            { country: String(updated.country || country) }
        );
    }

    // Si necesito city/postcode pero aún no hay ciudades cargadas -> difiere y trae
    if ((city || postcode) && cities.length === 0 && (updated.state || state)) {
        setPendingAutocomplete(norm);
        await fetchCities({ country: String(updated.country || country), state: String(updated.state || state) });
        return;
    }

    // City / Postcode (con cities ya cargadas)
    if (cities.length > 0) {
        const cityInput = typeof city === 'string' ? city : updated.verbose?.city;
        if ((!updated.city || !isOptionValue(updated.city, selectOptions['city'] as OptionType[] | undefined)) && typeof cityInput === 'string') {
            await assignValue(updated, 'city', cityInput, selectOptions['city'] as OptionType[]);
            if (updated.city) {
                const postOpts = buildPostcodeOpts(cities, String(updated.city));
                setSelectOptions((prev) => mergeSelectOptions(prev, { postcode: postOpts }));
            }
        }
        const postcodeInput = typeof postcode === 'string' ? postcode : String(updated.verbose?.postcode ?? '');
        if ((!updated.postcode || !isOptionValue(updated.postcode, selectOptions['postcode'] as OptionType[] | undefined)) && postcodeInput) {
            await assignValue(updated, 'postcode', postcodeInput, selectOptions['postcode'] as OptionType[]);
            if (updated.postcode) {
                const cityOpts = buildCityOptsByName(cities, String(updated.postcode));
                setSelectOptions((prev) => mergeSelectOptions(prev, { city: cityOpts }));
            }
        }
    }

    // Aplicar cambios si hubo
    if (JSON.stringify(updated) !== JSON.stringify(editedAddress)) {
        logInfo('handleFormAutoComplete - apply', updated);
        setEditedAddress(updated);
        setLocalInitAddress?.(updated);
        setKey?.((k) => k + 1);
    }
};

// ------------------------- Campos base -------------------------

const AddressFormFields: FieldType[] = [
    { label: 'Nombre', name: 'first_name', type: 'text', inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Ingresá tu nombre.' }] }, colProps: { xs: 12 } },
    { label: 'Apellido', name: 'last_name', type: 'text', inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Ingresá tu apellido.' }] }, colProps: { xs: 12 } },
    { label: 'País', name: 'country', type: 'select-with-search', colProps: { xs: 24, md: 8 }, inputProps: { disabled: true } },
    { label: 'Provincia', name: 'state', type: 'select-with-search', colProps: { xs: 12, md: 8 }, inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Seleccioná una provincia.' }] } },
    { label: 'Localidad', name: 'city', type: 'select-with-search', colProps: { xs: 12, md: 8 }, inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Seleccioná una localidad.' }] }, many: true },
    { label: 'Dirección', name: 'street', type: 'text', colProps: { xs: 24, md: 16, xl: 12 }, inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Ingresá una dirección.' }] } },
    { label: 'Depto/Piso', name: 'addr_detail', type: 'text', colProps: { xs: 12, md: 8, xl: 6 } },
    { label: 'Código Postal', name: 'postcode', type: 'select-with-search', colProps: { xs: 12, md: 8, xl: 6 }, inputProps: { required: true, autoComplete: 'postcode' }, formItemProps: { rules: [{ required: true, message: 'Seleccioná un código postal.' }] } },
    { label: 'Celular', name: 'phone_number', type: 'phonenumber', colProps: { xs: 24, md: 16 }, inputProps: { required: true }, formItemProps: { rules: [{ required: true, message: 'Ingresá un celular.' }] } },
];
/**
const groupCitiesByName = ({
    cities,
    filterByPostCode,
    filterByPostCodeKeys,
}: { cities: CityDataType[], filterByPostCode?: number, filterByPostCodeKeys?: string, }
): { name: string; keys: string[] }[] => {
    // logInfo('********* groupCitiesByName - filterByCityKeys: ', filterByPostCodeKeys,'cities: ', cities);

    const cityMap: { [key: string]: string[] } = {};

    // Filtrar las ciudades si se proporciona un código postal
    const filteredCitiesTemp = filterByPostCode
        ? cities.filter(city => city.codigo_postal === filterByPostCode)
        : cities;

    // Filtrar las ciudades si se proporciona una lista de keys de localidad
    const filteredCities = filterByPostCodeKeys
        ? filteredCitiesTemp.filter(city => filterByPostCodeKeys.split('-').includes(`${city.id_localidad}`))
        : filteredCitiesTemp;

    // Agrupar las ciudades por nombre
    filteredCities.forEach((city) => {
        if (!cityMap[city.nombre]) {
            cityMap[city.nombre] = [];
        }
        cityMap[city.nombre].push(`${city.id_localidad}`);
    });

    // Convertir el cityMap en un array y ordenarlo por nombre
    return Object.keys(cityMap)
        .map((name) => ({
            name,
            keys: cityMap[name],
        }))
        .sort((a, b) => a.name.localeCompare(b.name)); // Ordenar por nombre
};


const groupCitiesByPostCode = ({cities, filterByCity,filterByCityKeys,}:
    { cities: CityDataType[], filterByCity?: string, filterByCityKeys?: string, }
): { codigo_postal: string; keys: string[] }[] => {

    const cityMap: { [key: string]: string[] } = {};

    // Filtrar las ciudades si se proporciona un nombre de localidad
    const filteredCitiesTemp = filterByCity
        ? cities.filter(city => city.nombre === filterByCity)
        : cities;

    // Filtrar las ciudades si se proporciona una lista de keys de localidad
    const filteredCities = filterByCityKeys
        ? filteredCitiesTemp.filter(city => filterByCityKeys.split('-').includes(`${city.id_localidad}`))
        : filteredCitiesTemp;

    // logInfo('********* groupCitiesByPostCode - filterByCityKeys: ', filterByCityKeys,'filteredCities: ', filteredCities);

    filteredCities.forEach((city) => {
        if (!cityMap[city.codigo_postal]) {
            cityMap[city.codigo_postal] = [];
        }
        cityMap[city.codigo_postal].push(`${city.id_localidad}`);
    });

    return Object.keys(cityMap).map((codigo_postal) => ({
        codigo_postal,
        keys: cityMap[codigo_postal],
    }));
};
*/
// ------------------------- AddressForm -------------------------

interface AddressFormProps {
    prefixKey?: string;
    initAddress?: Address;
    onChange?: (name: string, value: unknown, label?: LabelType, index?: number) => void;
    className?: string;
    style?: React.CSSProperties;
    onSubmit?: (address: Address) => void;
    onCancel?: () => void;
    showActions?: boolean;
    submitLabel?: string;
    cancelLabel?: string;
}

const mergeWithDefaults = (source?: Address): Address => {
    const base: Address = {
        ...initAddressValues,
        verbose: { ...initAddressValues.verbose },
    };

    if (!source) {
        return base;
    }

    return {
        ...base,
        ...source,
        verbose: {
            city: source.verbose?.city ?? base.verbose?.city ?? '',
            state: source.verbose?.state ?? base.verbose?.state ?? '',
            postcode: source.verbose?.postcode ?? base.verbose?.postcode ?? '',
            country: source.verbose?.country ?? base.verbose?.country ?? '',
        },
    };
};

const AddressFormComponent: React.FC<AddressFormProps> = ({
    initAddress,
    onChange,
    prefixKey,
    className,
    style,
    onSubmit,
    onCancel,
    showActions = true,
    submitLabel,
    cancelLabel,
}) => {
    const initialAddress = useMemo(() => mergeWithDefaults(initAddress), [initAddress]);
    const tel = useSelector((state: RootState) => state.user.profile?.phone);
    const [localInitAddress, setLocalInitAddress] = useState<Address>(initialAddress);
    const [editedAddress, setEditedAddress] = useState<Address>(initialAddress);
    const [cities, setCities] = useState<CityDataType[]>([]);
    const [countries, setCountries] = useState<CountryDataType[]>([]);
    const [selectOptions, setSelectOptions] = useState<OptsMap>({ country: [{ label: 'Argentina', value: 'AR' }] });
    const [key, setKey] = useState(0);

    // Autocomplete diferido
    const [pendingAutocomplete, setPendingAutocomplete] = useState<{ [key: string]: FieldValue } | null>(null);

    // Campos
    const [fieldsList, setFieldsList] = useState(() => AddressFormFields.map((f) => ({ ...f, options: f.options })));
    const [disabledFields, setDisabledFields] = useState<{ [key: string]: boolean }>({});
    const [form] = Form.useForm();

    // anti-reproceso para autocomplete
    const lastAutoHashRef = useRef<string>('');

    // --------- Effects base ---------

    // Idempotente: solo cambia fieldsList si realmente cambian las options
    useEffect(() => {
        setFieldsList((prev) => {
            let changed = false;
            const next = prev.map((f) => {
                const newOpts = selectOptions[f.name];
                if (!shallowEqOptions(f.options as OptionType[], newOpts as OptionType[])) {
                    changed = true;
                    return { ...f, options: newOpts ?? f.options };
                }
                return f;
            });
            if (changed) setKey((k) => k + 1);
            return changed ? next : prev;
        });
    }, [selectOptions]);

    useEffect(() => {
        fetchCountries().then();
        if (initialAddress.country) {
            fetchStates({ country: initialAddress.country as string }).then();
        } else {
            // No forzamos valores internos aquí; delegamos al flujo normal
            handleChange('country', 'AR');
        }
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setLocalInitAddress(initialAddress);
        setEditedAddress(initialAddress);
    }, [initialAddress]);

    useEffect(() => {
        if (tel) {
            setLocalInitAddress((prev) => (prev.phone_number === tel ? prev : { ...prev, phone_number: tel }));
        }
    }, [tel]);

    useEffect(() => {
        if (countries.length > 0) {
            setSelectOptions((prev) =>
                mergeSelectOptions(prev, {
                    country: countries.map((c: CountryDataType) => ({ label: c.printable_name, value: c.iso_3166_1_a2 })),
                })
            );
        }
    }, [countries]);

    // Entraron ciudades => set options y procesa pendiente
    useEffect(() => {
        if (cities.length > 0) {
            setDisabledFields((p) => ({ ...p, city: false, postcode: false }));

            const cityOpts = buildCityOptsByName(cities);
            const postcodeOpts = buildPostcodeOpts(cities);

            // ✅ Aquí es donde realmente se cargan las opciones después del fetch
            setSelectOptions((prev) => mergeSelectOptions(prev, {
                city: cityOpts,
                postcode: postcodeOpts
            }));

            // Procesa autocomplete pendiente si existe
            if (pendingAutocomplete) {
                // procesar después de que GenericForm reciba las nuevas options
                const payload = pendingAutocomplete;
                setPendingAutocomplete(null);
                setTimeout(() => { void handleFormAutoComplete(payload); }, 80);
            }
        }
    }, [cities]);   // eslint-disable-line react-hooks/exhaustive-deps

    // --------- Fetchers ---------

    const fetchCountries = useCallback(async () => {
        try {
            const response = await conns<CountryDTO[]>('generics:location:get_country_datalist').get();
            setCountries(mapCountries(response.data));
        } catch (error) {
            console.error('Failed to fetch countries:', error);
        }
    }, []);

    const fetchStates = useCallback(async ({ country }: { country?: string }) => {
        if (!country) return;
        try {
            const response = await conns<StateDTO[]>('generics:location:get_state_datalist').get({ country });
            const opts = mapStatesToOptions(response.data);
            setSelectOptions((prev) => mergeSelectOptions(prev, { state: opts }));
        } catch (error) {
            console.error('Failed to fetch states:', error);
        }
    }, []);

    const fetchCities = useCallback(async ({ country, state }: { country?: string; state?: string }) => {
        if (!country || !state) return;
        try {
            setDisabledFields((prev) => ({ ...prev, city: true, postcode: true }));
            const response = await conns<CityDataType[]>('generics:location:get_postcode_datalist').get({ country, state });
            logInfo('   ---> fetchCities: response.data: ', response.data);
            setCities(response.data);

            // Las opciones se actualizarán en el useEffect de cities

        } catch (error) {
            console.error('Failed to fetch cities:', error);
        }
    }, []);

    const resetCityPostcodeOptions = () => {
        const allCities = buildCityOptsByName(cities);
        const allPost = buildPostcodeOpts(cities);
        setSelectOptions((prev) => mergeSelectOptions(prev, { city: allCities, postcode: allPost }));
    };

    // --------- Handlers ---------

    const handleChange = useCallback(
        (name: string, value: FieldValue, label?: LabelType) =>
            handleChangeHelper({
                name,
                value,
                label,
                editedAddress,
                setEditedAddress,
                cities,
                setSelectOptions,
                setCities,
                onChange: onChange,
                fetchStates,
                fetchCities,
                resetCityPostcodeOptions,
            }), [editedAddress, cities, onChange, fetchStates, fetchCities]    // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleFormAutoComplete = useCallback(
        async (formValues: { [key: string]: FieldValue }) => {
            await handleFormAutoCompleteHelper({
                formValues,
                editedAddress,
                setEditedAddress,
                setLocalInitAddress,
                selectOptions,
                setSelectOptions,
                cities,
                fetchStates,
                fetchCities,
                setPendingAutocomplete,
                lastAutoHashRef,
                setKey,
            });
        },
        [editedAddress, selectOptions, cities, fetchStates, fetchCities]
    );

    const handleSubmit = async () => {
        try {
            await form.validateFields();

            // GenericForm usa nombres internos (por ejemplo `account-address-4_city`).
            // Mezclamos esos valores con el estado para que el payload no dependa de
            // que el último cambio del selector haya terminado de propagarse.
            const formValues = normalizeKeys(form.getFieldsValue(true)) as Partial<Address>;
            const payload = buildAddressPayload({ ...editedAddress, ...formValues });

            if (!payload.city) {
                const cityIndex = AddressFormFields.findIndex((field) => field.name === 'city');
                const cityName = `${prefixKey ? `${prefixKey}-${cityIndex}` : cityIndex}_city`;
                form.setFields([{ name: cityName, errors: ['Seleccioná una localidad.'] }]);
                return;
            }

            if (onSubmit) {
                await Promise.resolve(onSubmit(payload));
                return;
            }

            const addressResponse = payload.id
                ? await conns<Address>('users:address', [payload.id]).put(payload)
                : await conns<Address>('users:address').post(payload);
            logInfo('addressResponse.data: ', addressResponse.data);
            setEditedAddress(addressResponse.data);
        } catch (error) {
            // Ant Design muestra los errores de cada campo mediante sus Form.Item.
            // Los errores de red/API quedan disponibles para el componente padre.
            if (!(error as { errorFields?: unknown }).errorFields) {
                console.error('Failed to submit form:', error);
            }
        }
    };

    return (
        // CAMBIO (1): agregamos onFinishFailed para ver errores de validación que bloqueen el submit.
        <Form
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            onFinishFailed={(err) => {
                // Explicación: si algún Form.Item tiene error, onFinish no se dispara.
                // Este log ayuda a detectar rápidamente qué campo lo está bloqueando.
                logError('Form submit failed:', err);
            }}
            className={className}
            style={style}
        >
            <GenericForm
                key={`${prefixKey}_${key}`}
                prefixKey={prefixKey}
                formFieldsList={fieldsList}
                initFieldsValues={sanitizeSelects(stripNullsDeep(localInitAddress)) as { [key: string]: FieldValue }}
                onChange={handleChange}
                handleGlobalAutoComplete={handleFormAutoComplete}
                disabledFields={disabledFields}
            />
            {showActions ? (
                <Row className='justify-content-end mt-3'>
                    {onCancel && (
                        <Button onClick={onCancel} size='small' type='dashed' className='me-4 mr-4 rounded-pill px-3 py-0'>
                            {cancelLabel ?? 'Cancelar'}
                        </Button>
                    )}
                    {/* ⬇️ CAMBIO: Desactivamos el efecto "wave" SOLO para este botón (bug de antd v5 en React 19).
                            Además usamos htmlType="button" + onClick={form.submit()} para forzar el envío y
                            evitar conflictos con formularios anidados o el autofill del navegador. */}
                    <Button
                        size='small'
                        className='rounded-pill px-3 py-0 hover_info hover_border_0'
                        htmlType='button'
                        onClick={() => form.submit()}
                    >
                        {submitLabel ?? 'Guardar'}
                    </Button>
                </Row>
            ) : null}
        </Form>
    );
};

export const AddressForm = React.memo(AddressFormComponent);
AddressForm.displayName = 'AddressForm';

// ------------------------- ShippingAddressForm -------------------------

interface ShippingAddressFormProps {
    onChange?: (selectedAddress: Address | undefined) => void;
    className?: string;
    style?: React.CSSProperties;
    onAutoSelectNext?: () => void;
    initialAddressId?: number;
}

export const ShippingAddressForm: React.FC<ShippingAddressFormProps> = ({
    onChange,
    className,
    style,
    onAutoSelectNext,
    initialAddressId,
}) => {
    const dispatch = useAppDispatch();
    const addresses = useSelector((state: RootState) => state.user.addresses);
    const guestToken = useSelector((state: RootState) => state.user.guestToken);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

    const [selectedAddressId, setSelectedAddressId] = useState<number | undefined>(initialAddressId);
    const [selectedAddress, setSelectedAddress] = useState<Address | undefined>();
    const [editMode, setEditMode] = useState(false);
    const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

    const canManageAddresses = isAuthenticated || Boolean(guestToken);

    const resolveAddressValue = (addr?: Address) => {
        if (!addr) return undefined;
        const guestId = (addr as { guestAddressId?: number }).guestAddressId;
        return guestId !== undefined ? Number(guestId) : addr.id;
    };

    useEffect(() => {
        if (!canManageAddresses) {
            setSelectedAddressId(undefined);
            setSelectedAddress(undefined);
            onChange?.(undefined);
            return;
        }
        if (addresses.length === 0) {
            void dispatch(fetchAddresses());
        }
    }, [dispatch, canManageAddresses, addresses.length, onChange]);

    useEffect(() => {
        if (!canManageAddresses) {
            return;
        }
        if (addresses.length === 0) {
            setSelectedAddressId(undefined);
            setSelectedAddress(undefined);
            return;
        }
        if (selectedAddressId === undefined) {
            setSelectedAddress(undefined);
            return;
        }
        const current = addresses.find(
            (addr) => resolveAddressValue(addr) === selectedAddressId,
        );
        if (current) {
            setSelectedAddress(current);
            return;
        }
        setSelectedAddressId(undefined);
        setSelectedAddress(undefined);
    }, [addresses, canManageAddresses, selectedAddressId]);

    // Sincronizar ID inicial cuando cambia externamente (restauración)
    useEffect(() => {
        if (typeof initialAddressId === 'number' && selectedAddressId === undefined) {
            setSelectedAddressId(initialAddressId);
        }
    }, [initialAddressId, selectedAddressId]);

    const { markManualSelection: markManualAddressSelection } = useAutoSelectSingleOption<Address, number>({
        options: canManageAddresses ? addresses : [],
        selectedValue: selectedAddressId,
        isLoading: !canManageAddresses,
        getOptionValue: (addr) => resolveAddressValue(addr),
        onSelect: (value, address) => {
            setSelectedAddressId(value);
            setSelectedAddress(address);
        },
        onAutoSelect: () => {
            onAutoSelectNext?.();
        },
        disabled: !canManageAddresses,
    });

    useEffect(() => {
        onChange?.(selectedAddress);
    }, [selectedAddress, onChange]);

    const handleAddressChange = (value: number | 'new') => {
        markManualAddressSelection();
        if (value === 'new') {
            setIsNewAddressModalOpen(true);
            return;
        }
        const numericValue = Number(value);
        const address = addresses.find((a) => resolveAddressValue(a) === numericValue);
        setSelectedAddressId(numericValue);
        setSelectedAddress(address);
        setEditMode(false);
    };

    const handleNewAddressSubmit = async (address: Address) => {
        setIsNewAddressModalOpen(false);
        const action = await dispatch(editAddress(address));
        if (editAddress.fulfilled.match(action)) {
            const saved = action.payload;
            const resolved = resolveAddressValue(saved);
            setSelectedAddressId(resolved);
            setSelectedAddress(saved);
        }
    };

    const handleEditAddress = async (updatedAddress: Address) => {
        const payload = selectedAddress ? { ...selectedAddress, ...updatedAddress } : updatedAddress;
        const action = await dispatch(editAddress(payload as Address));
        if (editAddress.fulfilled.match(action)) {
            const saved = action.payload;
            setSelectedAddress(saved);
            setSelectedAddressId(resolveAddressValue(saved));
        }
        logInfo('handleEditAddress - updatedAddress:', updatedAddress);
        setEditMode(false);
    };



    const getSelectedAddress = () => selectedAddress;

    const handleOnClick = () => {
        if (!canManageAddresses) {
            return;
        }
        if (addresses.length === 0) {
            setIsNewAddressModalOpen(true);
        }
    };

    return (
        <Card
            title='Dirección de envío'
            className={className}
            style={style}
        >
            {!canManageAddresses ? (
                <Alert
                    type='info'
                    title='Ingresá tu email y teléfono para gestionar tus direcciones.'
                    className='mb-3'
                    showIcon
                />
            ) : null}

            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <Select
                    style={{ flex: 1, height: '100%' }}
                    value={selectedAddressId}
                    onChange={handleAddressChange}
                    onClick={handleOnClick}
                    placeholder='Elije una dirección de envío'
                    disabled={!canManageAddresses}
                    optionLabelProp="label"
                >
                    {addresses && addresses.map(address => {
                        const value = resolveAddressValue(address);
                        if (value === undefined) return null;
                        return (
                            <Select.Option key={value} value={value} label={`${address.street} ${address.addr_detail || ''}`}>
                                {`${address.first_name} ${address.last_name}, ${address.street}`}
                            </Select.Option>
                        );
                    })}
                    <Select.Option value='new' label="Nueva dirección"><PlusOutlined /> Nueva dirección</Select.Option>
                </Select>

                {selectedAddress && canManageAddresses && (
                    <>
                        <Tooltip title="Ver detalles">
                            <Button
                                icon={<InfoCircleOutlined />}
                                type="text"
                                onClick={() => setIsInfoModalOpen(true)}
                            />
                        </Tooltip>
                        <Tooltip title="Editar dirección">
                            <Button
                                icon={<EditOutlined />}
                                type="text"
                                onClick={() => setEditMode(true)}
                            />
                        </Tooltip>
                    </>
                )}
            </div>

            <Modal
                title='Editar Dirección'
                open={editMode}
                onCancel={() => setEditMode(false)}
                footer={null}
                destroyOnHidden
            >
                <AddressForm
                    initAddress={getSelectedAddress() || initAddressValues}
                    onChange={() => { }}
                    onSubmit={handleEditAddress}
                    onCancel={() => setEditMode(false)}
                    submitLabel="Guardar Cambios"
                />
            </Modal>

            <Modal
                title='Detalle de Dirección'
                open={isInfoModalOpen}
                onCancel={() => setIsInfoModalOpen(false)}
                footer={null}
            >
                <ModernAddressDetails
                    address={getSelectedAddress()}
                    className='border-0 shadow-none'
                />
            </Modal>

            <Modal
                title='Nueva dirección'
                open={isNewAddressModalOpen}
                onCancel={() => setIsNewAddressModalOpen(false)}
                footer={null}
                destroyOnHidden
            >
                <AddressForm
                    initAddress={initAddressValues}
                    onSubmit={handleNewAddressSubmit}
                />
            </Modal>
        </Card>
    );
};

// ------------------------- BranchAddressForm -------------------------


// Campos específicos para direcciones de sucursal/negocio
const BranchAddressFormFields: FieldType[] = [
    { label: 'Nombre de Sucursal', name: 'branch_name', type: 'text', inputProps: { required: false }, colProps: { xs: 24 } },
    { label: 'País', name: 'country', type: 'select-with-search', colProps: { xs: 24, md: 8 }, inputProps: { disabled: true } },
    { label: 'Provincia', name: 'state', type: 'select-with-search', colProps: { xs: 12, md: 8 }, inputProps: { required: true } },
    { label: 'Localidad', name: 'city', type: 'select-with-search', colProps: { xs: 12, md: 8 }, inputProps: { required: true }, many: true },
    { label: 'Dirección', name: 'street', type: 'text', colProps: { xs: 24, md: 16, xl: 12 }, inputProps: { required: true } },
    { label: 'Detalle/Referencia', name: 'addr_detail', type: 'text', colProps: { xs: 12, md: 8, xl: 6 } },
    { label: 'Código Postal', name: 'postcode', type: 'select-with-search', colProps: { xs: 12, md: 8, xl: 6 }, inputProps: { required: true, autoComplete: 'postcode' } },
];

// Props del formulario de sucursal
interface BranchAddressFormProps {
    initial?: Address;
    onSubmit?: (address: Address) => void | Promise<void>;
    onCancel?: () => void;
    prefixKey?: string;
    className?: string;
    style?: React.CSSProperties;
    onChange?: (name: string, value: unknown, label?: LabelType, index?: number) => void;
}

const ADDRESS_PAYLOAD_FIELDS = [
    'id', 'first_name', 'last_name', 'street', 'addr_detail', 'city', 'state',
    'postcode', 'country', 'notes', 'phone_number', 'verbose',
] as const;

/** Construye exclusivamente el contrato de dirección aceptado por la API. */
export const buildAddressPayload = (
    source: Partial<Address> & { branch_name?: string },
): Address => {
    const payload: Partial<Address> = {};
    for (const field of ADDRESS_PAYLOAD_FIELDS) {
        const value = source[field];
        if (value !== undefined && value !== null && value !== '') {
            (payload as Record<string, unknown>)[field] = typeof value === 'string' ? value.trim() : value;
        }
    }
    payload.first_name = source.branch_name?.trim() || source.first_name?.trim() || '';
    payload.last_name = source.last_name?.trim() || '';
    payload.street = source.street?.trim() || '';
    payload.city = typeof source.city === 'string' ? source.city.trim() : source.city;
    payload.country = source.country?.trim() || 'AR';
    return payload as Address;
};

/** Convierte nombres internos de GenericForm (`4_street`) a nombres lógicos. */
export const buildBranchAddressPayload = (
    source: Partial<Address> & { branch_name?: string },
    formValues: { [key: string]: FieldValue },
): Address => {
    const logicalValues = normalizeKeys(formValues);
    return buildAddressPayload({
        ...source,
        ...logicalValues,
        branch_name: String(logicalValues.branch_name ?? source.branch_name ?? ''),
    });
};

export const BranchAddressForm: React.FC<BranchAddressFormProps> = ({
    initial,
    onSubmit,
    onCancel,
    prefixKey,
    className,
    style,
    onChange,
}) => {
    const initialSan = sanitizeSelects((initial ?? {}) as Address);

    const [editedAddress, setEditedAddress] = useState<Address & { branch_name?: string }>(() => ({
        ...initialSan,
        branch_name: initialSan?.first_name ?? '',
        country: initialSan?.country ?? 'AR',
    } as Address & { branch_name?: string }));

    const [localInitAddress, setLocalInitAddress] = useState<Address & { branch_name?: string }>(() => ({ ...editedAddress }));
    const [cities, setCities] = useState<CityDataType[]>([]);
    const [countries, setCountries] = useState<CountryDataType[]>([]);
    const [selectOptions, setSelectOptions] = useState<OptsMap>({ country: [{ label: 'Argentina', value: 'AR' }] });
    const [key, setKey] = useState(0);
    const [loadingCities, setLoadingCities] = useState(false);
    const [pendingAutocomplete, setPendingAutocomplete] = useState<{ [key: string]: FieldValue } | null>(null);
    const [fieldsList, setFieldsList] = useState(() => BranchAddressFormFields.map((f) => ({ ...f, options: f.options })));
    const [disabledFields, setDisabledFields] = useState<{ [key: string]: boolean }>({});
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    // Evita re-entradas en auto-complete (bloquea mismas combinaciones)
    const lastAutoHashRef = useRef<string>('');

    // Sincroniza options en campos (idempotente)
    useEffect(() => {
        setFieldsList((prev) => {
            let changed = false;
            const next = prev.map((f) => {
                const newOpts = (selectOptions[f.name] ?? f.options) as (OptionType[] | TreeOptionFieldType[]);
                if (!shallowEqOptions(f.options, newOpts)) {
                    changed = true;
                    return { ...f, options: newOpts } as typeof f;
                }
                return f;
            });
            if (changed) setKey((k) => k + 1);
            return changed ? next : prev;
        });
    }, [selectOptions]);

    // Cargar datos iniciales (país/estado/ciudades)
    useEffect(() => {
        (async () => {
            try {
                const resp = await conns<CountryDTO[]>('generics:location:get_country_datalist').get();
                setCountries(mapCountries(resp.data));
            } catch (e) {
                console.error('Failed to fetch countries:', e);
            }
            if (editedAddress?.country) {
                await fetchStates({ country: editedAddress.country as string });
            }
            if (editedAddress?.country && editedAddress?.state) {
                await fetchCities({ country: editedAddress.country as string, state: editedAddress.state as string });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poblá opciones de país cuando haya data
    useEffect(() => {
        if (countries.length > 0) {
            setSelectOptions((prev) =>
                mergeSelectOptions(prev, {
                    country: countries.map((c) => ({ label: c.printable_name, value: c.iso_3166_1_a2 })),
                })
            );
        }
    }, [countries]);

    // Si cambian ciudades, recalcular opciones y procesar autocomplete pendiente
    useEffect(() => {
        if (cities.length > 0) {
            setDisabledFields((p) => ({ ...p, city: false, postcode: false }));

            const cityOpts = buildCityOptsByName(cities);
            const postcodeOpts = buildPostcodeOpts(cities);

            // ✅ Aquí es donde realmente se cargan las opciones después del fetch
            setSelectOptions((prev) => mergeSelectOptions(prev, {
                city: cityOpts,
                postcode: postcodeOpts
            }));

            // Procesa autocomplete pendiente si existe
            if (pendingAutocomplete) {
                const payload = pendingAutocomplete;
                setPendingAutocomplete(null);
                setTimeout(() => { void handleFormAutoComplete(payload); }, 80);
            }
        }
    }, [cities]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch de estados/ciudades
    const fetchStates = async ({ country }: { country?: string }) => {
        if (!country) return;
        try {
            const response = await conns<StateDTO[]>('generics:location:get_state_datalist').get({ country });
            const opts = mapStatesToOptions(response.data);
            setSelectOptions((prev) => mergeSelectOptions(prev, { state: opts }));
        } catch (error) {
            console.error('Failed to fetch states:', error);
        }
    };

    const fetchCities = async ({ country, state }: { country?: string; state?: string }) => {
        if (!country || !state) return;
        try {
            setLoadingCities(true);
            setDisabledFields((prev) => ({ ...prev, city: true, postcode: true }));
            const response = await conns<CityDataType[]>('generics:location:get_postcode_datalist').get({ country, state });
            logInfo('   ---> fetchCities: response.data: ', response.data);
            setCities(response.data);
        } catch (error) {
            console.error('Failed to fetch cities:', error);
        } finally {
            setLoadingCities(false);
        }
    };

    const resetCityPostcodeOptions = () => {
        const allCities = buildCityOptsByName(cities);
        const allPost = buildPostcodeOpts(cities);
        setSelectOptions((prev) => mergeSelectOptions(prev, {
            city: allCities,
            postcode: allPost,
        }));
    };

    // Cambio controlado por cada campo del GenericForm (reusa misma lógica que AddressForm)
    const handleChange = useCallback(
        (name: string, value: FieldValue, label?: LabelType) =>
            handleChangeHelper({
                name,
                value,
                label,
                editedAddress,
                setEditedAddress,
                cities,
                setSelectOptions,
                setCities,
                onChange: onChange,
                fetchStates,
                fetchCities,
                resetCityPostcodeOptions,
                // fetchStates/fetchCities estables en este scope
            }), [editedAddress, cities, onChange] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleFormAutoComplete = useCallback(
        async (formValues: { [key: string]: FieldValue }) => {
            await handleFormAutoCompleteHelper({
                formValues,
                editedAddress,
                setEditedAddress,
                setLocalInitAddress,
                selectOptions,
                setSelectOptions,
                cities,
                fetchStates,
                fetchCities,
                setPendingAutocomplete,
                lastAutoHashRef,
                setKey,
                loadingCities,
                initialGuardAddress: initialSan,
            });
        },
        [editedAddress, selectOptions, cities, fetchStates, fetchCities, loadingCities] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleFinish = async () => {
        try {
            const values = await form.validateFields();
            const payload = buildBranchAddressPayload(
                editedAddress,
                values as { [key: string]: FieldValue },
            );

            setLocalInitAddress(payload);
            setSubmitting(true);
            await onSubmit?.(payload);
        } catch {
            // antd ya muestra errores de validación
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            className={className}
            style={style}
            initialValues={sanitizeSelects(localInitAddress)}
            onFinish={handleFinish}
        >
            <GenericForm
                key={`${prefixKey}_${key}`}
                prefixKey={prefixKey}
                formFieldsList={fieldsList}
                initFieldsValues={sanitizeSelects(localInitAddress) as { [key: string]: FieldValue }}
                onChange={handleChange}
                handleGlobalAutoComplete={handleFormAutoComplete}
                disabledFields={disabledFields}
            />
            <Row justify="end" style={{ marginTop: 16 }}>
                <Space wrap>
                    {onCancel && <Button onClick={onCancel} disabled={submitting}>Cancelar</Button>}
                    <Button type="primary" htmlType="submit" loading={submitting}>Guardar dirección</Button>
                </Space>
            </Row>
        </Form>
    );
};
